/**
 * Multi-cohort retention curve overlay with a dashed "industry churn"
 * benchmark line. This is the centerpiece of the Retention tab — the
 * visual that backs the "90% unsubscribe within a year" anti-churn thesis.
 */
import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CATEGORICAL, SEMANTIC } from '../theme';
import { formatPercent } from '../format';

export interface CurvePoint {
  week_offset: number;
  retention_pct: number;
}

export interface CohortCurve {
  cohort_week: string;
  cohort_size: number;
  points: CurvePoint[];
}

export interface RetentionCurveChartProps {
  series: CohortCurve[];
  benchmark?: CurvePoint[];
  height?: number;
}

interface FlatPoint {
  week_offset: number;
  [cohort: string]: number;
}

export const RetentionCurveChart: React.FC<RetentionCurveChartProps> = ({
  series,
  benchmark,
  height = 320,
}) => {
  const { flat, cohortKeys, maxOffset } = useMemo(() => {
    const keys = series.map((s) => s.cohort_week);
    let maxOff = 0;
    const map = new Map<number, FlatPoint>();
    for (const s of series) {
      for (const p of s.points) {
        if (p.week_offset > maxOff) maxOff = p.week_offset;
        let row = map.get(p.week_offset);
        if (!row) {
          row = { week_offset: p.week_offset };
          map.set(p.week_offset, row);
        }
        row[s.cohort_week] = p.retention_pct;
      }
    }
    if (benchmark) {
      for (const p of benchmark) {
        if (p.week_offset > maxOff) maxOff = p.week_offset;
        let row = map.get(p.week_offset);
        if (!row) {
          row = { week_offset: p.week_offset };
          map.set(p.week_offset, row);
        }
        row['industry_benchmark'] = p.retention_pct;
      }
    }
    const flatArr = Array.from(map.values()).sort((a, b) => a.week_offset - b.week_offset);
    return { flat: flatArr, cohortKeys: keys, maxOffset: maxOff };
  }, [series, benchmark]);

  if (!series.length) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-gray-500">
        No cohort data yet.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={flat} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-muted)" vertical={false} />
        <XAxis
          dataKey="week_offset"
          type="number"
          domain={[0, maxOffset]}
          tick={{ fontSize: 11, fill: 'var(--text-secondary)', fontFamily: 'Inter, system-ui, sans-serif' }}
          axisLine={{ stroke: 'var(--border)' }}
          tickLine={false}
          label={{
            value: 'Weeks after signup',
            position: 'insideBottom',
            offset: -4,
            style: { fontSize: 10, fill: 'var(--text-tertiary)' },
          }}
        />
        <YAxis
          domain={[0, 100]}
          tick={{ fontSize: 11, fill: 'var(--text-secondary)', fontFamily: 'Inter, system-ui, sans-serif' }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v) => `${v}%`}
          width={42}
        />
        <Tooltip
          contentStyle={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontSize: 12,
            boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
          }}
          labelStyle={{ fontWeight: 600, color: 'var(--text-primary)' }}
          labelFormatter={(v) => `Week ${v}`}
          formatter={(value: number | string, name) => [
            typeof value === 'number' ? formatPercent(value) : value,
            String(name) === 'industry_benchmark'
              ? 'Industry benchmark'
              : formatCohortLabel(String(name)),
          ]}
        />
        <Legend
          iconType="line"
          wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
          formatter={(v) =>
            String(v) === 'industry_benchmark' ? (
              <span className="text-gray-500">Industry benchmark</span>
            ) : (
              <span className="text-gray-600">{formatCohortLabel(String(v))}</span>
            )
          }
        />
        <ReferenceLine y={50} stroke="var(--border)" strokeDasharray="2 4" />
        {cohortKeys.map((key, i) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            stroke={CATEGORICAL[i % CATEGORICAL.length]}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: CATEGORICAL[i % CATEGORICAL.length], fill: 'var(--surface)' }}
            connectNulls
            isAnimationActive={false}
          />
        ))}
        {benchmark && (
          <Line
            type="monotone"
            dataKey="industry_benchmark"
            stroke={SEMANTIC.neutral}
            strokeWidth={1.75}
            strokeDasharray="5 3"
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};

function formatCohortLabel(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.valueOf())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
